import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  InputChangeEventDetail,
  IonCol,
  IonInput,
  IonRow,
} from '@ionic/angular/standalone';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { DISP_CONSTANTS } from './header-search.constants';

@Component({
  selector: 'app-header-search',
  templateUrl: './header-search.component.html',
  styleUrls: ['./header-search.component.scss'],
  imports: [IonInput, IonCol, IonRow, CommonModule],
})
/**
 * HeaderSearchComponent
 *
 * A small, self-contained search input component built with Ionic's `IonInput`.
 * Designed to be embedded within a header (e.g., inside `app-table-header`) to
 * provide search functionality over a data table.
 *
 * Responsibilities:
 * - Render a text input (and optionally a cancel/clear affordance).
 * - Emit changes as the user types (`emitTextChange`).
 * - Clear the input programmatically and emit a clear event (`emitClearChange`).
 *
 * Events:
 * - `emitTextChange`: Emits the Ionic `InputChangeEventDetail` via the wrapped native event.
 * - `emitClearChange`: Emits when the input is cleared programmatically or via UI.
 *
 * Typical usage:
 * ```html
 * <app-header-search
 *   [isDisabled]="searchBarDisabled"
 *   (emitTextChange)="onHeaderTextChange($event)"
 *   (emitClearChange)="onHeaderSearchClear()"
 * ></app-header-search>
 * ```
 *
 * Parent handling (example):
 * ```ts
 * onHeaderTextChange(ev: CustomEvent<InputChangeEventDetail>) {
 *   const value = (ev.detail?.value ?? '').trim().toLowerCase();
 *   this.dataSource.filter = value;
 *   this.dataSource.paginator?.firstPage?.();
 *   this.updateDisplayStopGrouping();
 * }
 *
 * onHeaderSearchClear() {
 *   this.dataSource.filter = '';
 *   this.dataSource.paginator?.firstPage?.();
 *   this.updateDisplayStopGrouping();
 * }
 * ```
 */
export class HeaderSearchComponent {
  /**
   * Disables the search input when `true`.
   * Useful for preventing input while data is loading or the feature is unavailable.
   */
  @Input() isDisabled = false;

  /**
   * Displays entered text in uppercase when enabled.
   * Kept opt-in so shared table header searches preserve current behavior.
   */
  @Input() displayUppercase = false;

  /**
   * Emits the native Ionic input change event wrapper.
   * Payload: `CustomEvent<InputChangeEventDetail>`, where `detail.value` is the current text.
   *
   * Listener example:
   * ```ts
   * (emitTextChange)="onHeaderTextChange($event)"
   * ```
   */
  @Output() emitTextChange = new EventEmitter<
    CustomEvent<InputChangeEventDetail>
  >();

  /**
   * Emits when the search input is cleared (programmatically or via UI).
   * No payload; the parent should reset table filters/paginator on receipt.
   *
   * Listener example:
   * ```ts
   * (emitClearChange)="onHeaderSearchClear()"
   * ```
   */
  @Output() emitClearChange = new EventEmitter<void>();

  /**
   * Reference to the `IonInput` instance rendered in the template.
   * Used for imperative operations like clearing the input's value.
   *
   * Template requirement:
   * ```html
   * <ion-input #input ... ></ion-input>
   * ```
   */
  @ViewChild(IonInput) input!: IonInput;

  /** Image sources available to the template (icons, etc.). */
  readonly IMAGES = IMAGE_SRC;

  /** Display constants used for labels/placeholders in the template. */
  readonly DISP_CONSTANTS = DISP_CONSTANTS;

  /**
   * Toggles visibility of the cancel button in the UI (if implemented in the template).
   * This is a local view concern; not exposed to the parent by design.
   */
  showCancelButton = false;

  /**
   * Optional holder for template-local state or DI-bound component references.
   * Not currently used; reserved for future extensibility.
   */
  component: any;

  /**
   * Clears the search input's visible value and notifies the parent that
   * a clear has occurred.
   *
   * Side effects:
   * - Sets `ion-input.value = ''` without emitting an input event.
   * - Emits `emitClearChange` for the parent to clear datasource filters.
   */
  clearSearch(): void {
    this.input.value = '';
    this.emitClearChange.emit();
  }

  /**
   * Forwards the Ionic input change event to the parent.
   *
   * @param $event - The `CustomEvent<InputChangeEventDetail>` emitted by `IonInput`.
   *                 `detail.value` contains the current input string.
   *
   */
  sendTextChange($event: CustomEvent<InputChangeEventDetail>): void {
    this.emitTextChange.emit($event);
  }
}
